import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, Container, Paper, Typography, CircularProgress } from '@mui/material';
import { XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PAYMENT_FUNCTIONS } from '../config/functionRoutes';

const PaymentCancelPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [emailSent, setEmailSent] = useState(false);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const handlePaymentCancel = async () => {
      try {
        // Let the server select the correct initial template. This covers the
        // browser-return path when Stripe has not emitted a webhook yet.
        if (sessionId) {
          const { getFunctions, httpsCallable } = await import('firebase/functions');
          const functions = getFunctions(undefined, 'europe-west1');
          const queueConfirmationEmail = httpsCallable(functions, PAYMENT_FUNCTIONS.cancelConfirmation);
          const result = await queueConfirmationEmail({ sessionId });
          const response = result.data as { queued?: boolean; alreadyQueued?: boolean };

          if (response?.queued || response?.alreadyQueued) {
            setEmailSent(true);
            console.log('[PaymentCancelPage] Registration confirmation email queued or already queued');
          }
        }
      } catch (error) {
        console.error('[PaymentCancelPage] Error handling payment cancel:', error);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(() => {
      handlePaymentCancel();
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [sessionId]);

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Box sx={{ mb: 3 }}>
            <XCircle size={64} color="#f44336" />
          </Box>
          <Typography variant="h4" gutterBottom>
            {t('payment.cancel.title')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 2 }}>
            <CircularProgress size={20} sx={{ mr: 2 }} />
            <Typography variant="body1" color="text.secondary">
              {t('payment.cancel.processing')}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('payment.cancel.processingMessage')}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Box sx={{ mb: 3 }}>
          <XCircle size={64} color="#f44336" />
        </Box>
        <Typography variant="h4" gutterBottom>
          {t('payment.cancel.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {t('payment.cancel.message')}
        </Typography>
        {emailSent && (
          <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
            {t('payment.cancel.emailSent')}
          </Typography>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/my-registrations')}
          sx={{ mt: 2 }}
        >
          {t('payment.cancel.backToRegistrations')}
        </Button>
      </Paper>
    </Container>
  );
};

export default PaymentCancelPage;
